/** biome-ignore-all lint/correctness/useJsxKeyInIterable: no explanation */
import { assertNever } from '@sparkdotfi/common-universal'

import { ApproveActionRow } from '../../flavours/approve/ApproveActionRow'
import { ApproveDelegationActionRow } from '../../flavours/approve-delegation/ApproveDelegationActionRow'
import { BorrowActionRow } from '../../flavours/borrow/BorrowActionRow'
import { ClaimAirdropRewardsActionRow } from '../../flavours/claim-airdrop-rewards/ClaimSparkRewardsActionRow'
import { ClaimFarmRewardsActionRow } from '../../flavours/claim-farm-rewards/ClaimFarmRewardsActionRow'
import { ClaimMarketRewardsActionRow } from '../../flavours/claim-market-rewards/ClaimMarketRewardsActionRow'
import { ClaimPointsRewardsActionRow } from '../../flavours/claim-points-rewards/ClaimPointsRewardsActionRow'
import { ClaimSparkRewardsActionRow } from '../../flavours/claim-spark-rewards/ClaimSparkRewardsActionRow'
import { DelegateActionRow } from '../../flavours/delegate/DelegateActionRow'
import { DepositActionRow } from '../../flavours/deposit/DepositActionRow'
import { DepositToSavingsActionRow } from '../../flavours/deposit-to-savings/DepositToSavingsActionRow'
import { DepositToWethActionRow } from '../../flavours/deposit-to-weth/DepositToWethActionRow'
import { FinalizeSpkUnstakeActionRow } from '../../flavours/finalize-spk-unstake/FinalizeSpkUnstakeActionRow'
import { PermitActionRow } from '../../flavours/permit/PermitActionRow'
import { RepayActionRow } from '../../flavours/repay/RepayActionRow'
import { ResetApprovalActionRow } from '../../flavours/reset-approval/ResetApprovalActionRow'
import { SetUseAsCollateralActionRow } from '../../flavours/set-use-as-collateral/SetUseAsCollateralActionRow'
import { SetUserEModeActionRow } from '../../flavours/set-user-e-mode/SetUserEModeActionRow'
import { StakeActionRow } from '../../flavours/stake/StakeActionRow'
import { StakeSpkActionRow } from '../../flavours/stake-spk/StakeSpkActionRow'
import { SwapActionRow } from '../../flavours/swap/SwapActionRow'
import { UndelegateActionRow } from '../../flavours/undelegate/UndelegateActionRow'
import { UnstakeActionRow } from '../../flavours/unstake/UnstakeActionRow'
import { UnstakeSpkActionRow } from '../../flavours/unstake-spk/UnstakeSpkActionRow'
import { UpgradeSdaiActionRow } from '../../flavours/upgrade-sdai/UpgradeSdaiActionRow'
import { WithdrawActionRow } from '../../flavours/withdraw/WithdrawActionRow'
import { WithdrawFromSavingsActionRow } from '../../flavours/withdraw-from-savings/WithdrawFromSavingsActionRow'
import { WithdrawFromWethActionRow } from '../../flavours/withdraw-from-weth/WithdrawFromWethActionRow'
import { ActionHandler, BatchActionHandler } from '../../logic/types'
import { ActionsGridLayout } from '../../types'
import { BatchActionTrigger } from '../action-row/BatchActionTrigger'
import { ActionsGrid } from '../actions-grid/ActionsGrid'

interface ActionsProps {
  actionHandlers: ActionHandler[]
  batchActionHandler: BatchActionHandler | undefined
  layout: ActionsGridLayout
}

export function Actions({ actionHandlers, batchActionHandler, layout }: ActionsProps) {
  return (
    <div className="rounded-sm border border-primary">
      <ActionsGrid layout={layout}>
        {actionHandlers.map((handler, index) => {
          const props = {
            layout,
            key: index,
            actionIndex: index,
            ...(batchActionHandler
              ? {
                  actionHandlerState: { status: 'disabled' } as const,
                }
              : {
                  actionHandlerState: handler.state,
                  onAction: handler.onAction,
                }),
          }

          switch (handler.action.type) {
            case 'approve':
              return <ApproveActionRow action={handler.action} {...props} />
            case 'resetApproval':
              return <ResetApprovalActionRow action={handler.action} {...props} />
            case 'approveDelegation':
              return <ApproveDelegationActionRow action={handler.action} {...props} />
            case 'borrow':
              return <BorrowActionRow action={handler.action} {...props} />
            case 'deposit':
              return <DepositActionRow action={handler.action} {...props} />
            case 'permit':
              return <PermitActionRow action={handler.action} {...props} />
            case 'repay':
              return <RepayActionRow action={handler.action} {...props} />
            case 'setUseAsCollateral':
              return <SetUseAsCollateralActionRow action={handler.action} {...props} />
            case 'setUserEMode':
              return <SetUserEModeActionRow action={handler.action} {...props} />
            case 'withdraw':
              return <WithdrawActionRow action={handler.action} {...props} />
            case 'claimMarketRewards':
              return <ClaimMarketRewardsActionRow action={handler.action} {...props} />
            case 'withdrawFromSavings':
              return <WithdrawFromSavingsActionRow action={handler.action} {...props} />
            case 'depositToSavings':
              return <DepositToSavingsActionRow action={handler.action} {...props} />
            case 'upgradeSdai':
              return <UpgradeSdaiActionRow action={handler.action} {...props} />
            case 'stake':
              return <StakeActionRow action={handler.action} {...props} />
            case 'unstake':
              return <UnstakeActionRow action={handler.action} {...props} />
            case 'swap':
              return <SwapActionRow action={handler.action} {...props} />
            case 'claimFarmRewards':
              return <ClaimFarmRewardsActionRow action={handler.action} {...props} />
            case 'claimPointsRewards':
              return <ClaimPointsRewardsActionRow action={handler.action} {...props} />
            case 'claimSparkRewards':
              return <ClaimSparkRewardsActionRow action={handler.action} {...props} />
            case 'stakeSpk':
              return <StakeSpkActionRow action={handler.action} {...props} />
            case 'unstakeSpk':
              return <UnstakeSpkActionRow action={handler.action} {...props} />
            case 'finalizeSpkUnstake':
              return <FinalizeSpkUnstakeActionRow action={handler.action} {...props} />
            case 'claimAirdropRewards':
              return <ClaimAirdropRewardsActionRow action={handler.action} {...props} />
            case 'delegate':
              return <DelegateActionRow action={handler.action} {...props} />
            case 'undelegate':
              return <UndelegateActionRow {...props} />
            case 'depositToWeth':
              return <DepositToWethActionRow action={handler.action} {...props} />
            case 'withdrawFromWeth':
              return <WithdrawFromWethActionRow action={handler.action} {...props} />
            default:
              assertNever(handler.action)
          }
        })}
      </ActionsGrid>
      {batchActionHandler && <BatchActionTrigger batchActionHandler={batchActionHandler} actionsGridLayout={layout} />}
    </div>
  )
}
